import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalAction } from './modal-action.type';
import { ModalBackdropService } from './modal-backdrop.service';

@Directive({ selector: '[srModal]' })
export class ModalDirective implements OnInit, OnDestroy {

  @Input() materializeActions: EventEmitter<string | ModalAction> | null = null;
  @Input() dismissible = true;
  @Input() animationDuration = 250;

  @Output() opened = new EventEmitter<HTMLElement>();
  @Output() closed = new EventEmitter<HTMLElement>();

  private isOpen = false;
  private isAnimating = false;

  private unlistenEsc: (() => void) | null = null;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private actionSub!: Subscription;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private backdropService: ModalBackdropService
  ) { }

  ngOnInit(): void {
    this.initHostElement();

    if (this.materializeActions) {
      this.actionSub = this.materializeActions.subscribe(
        (action: string | ModalAction) => this.handleAction(action)
      );
    }
  }

  ngOnDestroy(): void {
    this.actionSub?.unsubscribe();
    if (this.openTimer) clearTimeout(this.openTimer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.removeEscListener();
    if (this.isOpen) this.backdropService.hide(this.animationDuration);
  }

  open(): void {
    if (this.isAnimating) return;
    if (this.isOpen) return;

    this.isOpen = true;
    this.isAnimating = true;

    const host = this.el.nativeElement;

    this.renderer.setStyle(host, 'display', 'block');

    void host.offsetHeight;

    this.renderer.addClass(host, 'modal--open');

    // Show shared backdrop — blocks outside clicks
    this.backdropService.show(
      this.dismissible ? () => this.close() : null,
      this.animationDuration
    );

    // ESC to dismiss
    if (this.dismissible) {
      this.attachEscListener();
    }

    // Emit opened after animation completes
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.isAnimating = false;
      this.openTimer = null;
      this.opened.emit(host);
    }, this.animationDuration);
  }

  close(): void {
    if (this.isAnimating) return;
    if (!this.isOpen) return;

    this.isOpen = false;
    this.isAnimating = true;

    const host = this.el.nativeElement;

    this.removeEscListener();
    this.renderer.removeClass(host, 'modal--open');
    this.backdropService.hide(this.animationDuration);

    if (this.closeTimer) clearTimeout(this.closeTimer);

    this.closeTimer = setTimeout(() => {
      this.renderer.setStyle(host, 'display', 'none');
      this.isAnimating = false;
      this.closeTimer = null;
      this.closed.emit(host);
    }, this.animationDuration);
  }

  private handleAction(action: string | ModalAction): void {
    const name = this.extractAction(action);
    if (name === 'open') this.open();
    if (name === 'close') this.close();
    if (!name) console.warn('[ModalDirective] unrecognized action:', action);
  }

  private extractAction(action: string | ModalAction): string | null {
    // Plain string: 'open' | 'close'
    if (typeof action === 'string') return action;

    // New pattern: { action: 'modal', params: ['open'] }
    if (action.action === 'modal' && action.params?.length) {
      return action.params[0];
    }

    // Direct pattern: { action: 'open' | 'close', params: [] }
    if (action.action === 'open' || action.action === 'close') {
      return action.action;
    }

    return null;
  }

  private initHostElement(): void {
    const host = this.el.nativeElement;
    this.renderer.addClass(host, 'modal-directive');
    this.renderer.setStyle(host, 'display', 'none');
    this.renderer.setStyle(host, 'transition',
      `opacity ${this.animationDuration}ms ease, ` +
      `transform ${this.animationDuration}ms ease`
    );
  }

  private attachEscListener(): void {
    this.removeEscListener();
    this.unlistenEsc = this.renderer.listen(
      'document', 'keydown.escape',
      () => { if (this.isOpen) this.close(); }
    );
  }

  private removeEscListener(): void {
    this.unlistenEsc?.();
    this.unlistenEsc = null;
  }
}
